import { Intent } from '@blueprintjs/core';
import { Button, LinkButton } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { Filter, FilterButton } from 'components/filtering';
import { FormListSelector, FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { Breadcrumbs, Tabs } from 'components/navigation';
import { NotificationToaster } from 'components/notifications';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { useDebounce } from 'hooks/useDebounce';
import { PageRestricted } from 'pages/errors/page-restricted';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AccountService, GreyFleetService } from 'services';
import { SetFiltering } from 'state/actions';
import { GreyFleetInsuranceModal } from '../validation';

export function GreyFleetInsuranceListing(
    {
        breadcrumbs,
        requiredAction,
        title
    }
) {
    const defaultPageNumber = 1;
    const defaultRecordSize = 50;
    const debounceTimeout = 750;
    const noDataMessage = "No insurance checks found";

    const pendingTableHeaders = ["ID", "Account", "Employee Name", "Vehicle Reg.", "AI Results", "Created date", "Modified date", "View Check"];
    const pendingSortableHeaders = [{headerName: "ID", sortName: "checkid"}, {headerName: "AI Results", sortName: "aistatus"}, {headerName: "Created date", sortName: "createddate"}];
    const completedTableHeaders = ["ID", "Account", "Employee Name", "Vehicle Reg.", "AI Results", "Completed on", "Results", "View Check"];
    const completedSortableHeaders = [{headerName: "ID", sortName: "checkid"}, {headerName: "AI Results", sortName: "aistatus"}, {headerName: "Completed on", sortName: "humanapprovaldate"}];

    const pageFilters = [{ "displayName": "AI Result", "name": "AIRESULT", "items": [
        { "id": "Fail", "name": "Fail" },
        { "id": "Valid", "name": "Valid" },
        { "id": "Inconclusive", "name": "Inconclusive" },
        { "id": "Awaiting", "name": "Awaiting" },
        { "id": "Error", "name": "Error" }
    ] }];

    const canAccessPage = UserHasAccess(requiredAction);

    const [tableHeaders, setTableHeaders] = useState(pendingTableHeaders);
    const [sortableHeaders, setSortableHeaders] = useState(pendingSortableHeaders);
    const [tabs, setTabs] = useState([
        { key: "PENDING", title: "Pending Insurance Checks", selected: true },
        { key: "COMPLETED", title: "Completed Insurance Checks" },
    ]);
    const [currentTab, setCurrentTab] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [accountsListOpened, setAccountsListOpened] = useState(false);
    const [accounts, setAccounts] = useState([]);

    const [selectedInsurance, setSelectedInsurance] = useState(null);
    const [insuranceList, setInsuranceList] = useState([]);
    const [showInsuranceModal, setShowInsuranceModal] = useState(false);

    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [pageNumber, setPageNumber] = useState(defaultPageNumber);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [searchTerm, setSearchTerm] = useState("");
    const [tableSortBy, setTableSortBy] = useState({ headerName: "ID", sortName: "checkid" });
    const [tableSortDir, setTableSortDir] = useState("A");
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    
    const searchReduxName = "insuranceListingSearch";
    const tabReduxName = "insuranceListingTab";
    const filtersReduxName = "insuranceListingFilters";
    const accountsReduxName = "insuranceListingAccounts";

    const reduxSearch = useSelector(state => state.filters[searchReduxName]);
    const reduxTab = useSelector(state => state.filters[tabReduxName]);
    const reduxFilters = useSelector(state => state.filters[filtersReduxName]);
    const reduxAccounts = useSelector(state => state.filters[accountsReduxName]);
    const dispatch = useDispatch();
    
    const searchInputRef = useRef(null);
    
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentFilters = useRef();
    const currentSelectedAccounts = useRef();
    const currentSelectedTab = useRef();

    const licenceCheckingFeatureId = 52;
    
    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        AccountService.getAllAccounts([licenceCheckingFeatureId ])
            .then(
                (response) => {
                    setAccounts(response.data.filter(a => a != null).map(a => ({ id: a.id, name: `${a.name} - ${a.memberNumber}` })));
                    focusOnTextBox(searchInputRef);
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not load accounts. Please refresh the page.");
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load accounts. Please refresh the page.");
            })
            .finally(() => {
                setLoadingData(false);
            });
    }, []);

    useEffect(() => {
        if (debouncedSearchTerm !== null && debouncedSearchTerm !== currentSearchTerm.current) {
            setPageNumber(1);
            // Only update Redux/search when debouncedSearchTerm changes
            dispatch(SetFiltering(searchReduxName, debouncedSearchTerm));
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (reduxSearch != null && reduxSearch.length > 0 && reduxSearch !== searchTerm) {
            setSearchTerm(reduxSearch);
        }
    }, [reduxSearch]);

    useEffect(() => {
        if (reduxTab != null && reduxTab !== currentTab) {
            changeTab(reduxTab);
        }
    }, [reduxTab]);

    useEffect(() => {
        if (reduxFilters != null) {
            setAppliedFilters(reduxFilters);
        }
    }, [reduxFilters]);

    useEffect(() => {
        if (reduxAccounts != null) {
            setSelectedAccounts(reduxAccounts);
        }
    }, [reduxAccounts]);
    
    useEffect(() => {
        if (!loadingData && !accountsListOpened) {
            if (
                currentPageNumber.current !== pageNumber ||
                currentPageSize.current !== pageSize ||
                currentSearchTerm.current !== debouncedSearchTerm ||
                currentTableSortBy.current !== tableSortBy ||
                currentTableSortDir.current !== tableSortDir ||
                currentFilters.current !== appliedFilters.length ||
                currentSelectedAccounts.current !== selectedAccounts.length ||
                currentSelectedTab.current !== currentTab
            )
            {
                currentPageNumber.current = pageNumber;
                currentPageSize.current = pageSize;
                currentSearchTerm.current = debouncedSearchTerm;
                currentTableSortBy.current = tableSortBy;
                currentTableSortDir.current = tableSortDir;
                currentFilters.current = appliedFilters.length;
                currentSelectedAccounts.current = selectedAccounts.length;
                currentSelectedTab.current = currentTab;

                loadListingData();
            }
        }
    }, [loadingData, accountsListOpened, pageSize, pageNumber, tableSortBy, tableSortDir, appliedFilters, debouncedSearchTerm, selectedAccounts, currentTab]);

    const nextInsuranceId = useMemo(() => {
        const selectedIndex = insuranceList.findIndex(r => r.checkId === selectedInsurance?.checkId);
        return selectedIndex >= 0 && selectedIndex < (insuranceList.length - 1)
            ? insuranceList[selectedIndex + 1].checkId
            : null;
    }, [selectedInsurance, insuranceList]);

    function focusOnTextBox(InputRef) {
        setTimeout(function () {
            if (InputRef.current != null) {
                InputRef.current.focus();
            }
        });
    }

    function changeTab(index) {
        if (index === currentTab) {
            return;
        }
        let clonedTabs = [
            ...tabs
        ];

        clonedTabs = clonedTabs.map(function (t, i) {
            return {
                ...t,
                selected: index === i
            }
        });

        setTabs(clonedTabs);
        setCurrentTab(index);
        setTableHeaders(index === 0 ? pendingTableHeaders : completedTableHeaders);
        
        const newSortableHeaders = index === 0 ? pendingSortableHeaders : completedSortableHeaders;
        setSortableHeaders(newSortableHeaders);
        setTableSortBy(newSortableHeaders[0]);
        setTableSortDir("A");
    }

    function onTabClick(index) {
        if (index === currentTab) {
            return;
        }
        dispatch(SetFiltering(tabReduxName, index));
    }
    
    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        // Do NOT dispatch here, let debouncedSearchTerm handle it
    }

    function onAccountsSelected(selectedItems) {
        dispatch(SetFiltering(accountsReduxName, selectedItems));
        setPageNumber(1);
    }

    function onAccountsListOpened() {
        setAccountsListOpened(true);
    }

    function onAccountsListClose() {
        setAccountsListOpened(false);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onTableSort(header, direction) {
        setTableSortBy(header);
        setTableSortDir(direction);
    }

    function onFilterChange(listingFilters) {
        setPageNumber(1);
        dispatch(SetFiltering(filtersReduxName, listingFilters));

        if (listingFilters.length > appliedFilters.length) {
            setShowFilters(true);
        }
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function openInsuranceCheckView(insurance) {
        setSelectedInsurance(insurance);
        setShowInsuranceModal(true);
    }

    function showNextInsuranceModal() {
        if (nextInsuranceId != null) {
            setSelectedInsurance(insuranceList.find(i => i.checkId === nextInsuranceId));
        }
    }

    function getFilters(){
        return [
            ...appliedFilters.map(f => ({
                key: f.name,
                value: f.value
            })),
            ...selectedAccounts.map(a => ({
                key: "ACCOUNT",
                value: a
            })),
            { key: "NOTAPPROVED", value: currentTab === 0 ? "true" : "false" }
        ];
    }

    function getColouredAIStatus(status) {
        let className;
        switch (status) {
            case "Valid":
                className = "text-intent-success";
                break;
            case "Awaiting":
                className = "text-intent-warning";
                break;
            case "Error":
                className = "text-intent-danger";
                break;
            case "Fail":
                className = "text-intent-danger";
                break;
            default:
                className = "text-intent-info";
        }

        return <span className={className}>{status}</span>;
    }

    function getHumanResult(humanApproved) {
        if (humanApproved === null) {
            return "N/A";
        }
        
        return humanApproved ? "Valid" : "Fail";
    }

    function loadListingData() {
        if (!canAccessPage) {
            return;
        }
        
        setLoadingData(true);
        GreyFleetService.SearchInsuranceDocuments(pageNumber, pageSize, searchTerm, tableSortBy.sortName, tableSortDir, getFilters())
            .then(
                (response) => {
                    setInsuranceList(response.data);
                    setTotalRecords(response.totalCount);
                    setTableRows(response.data.map(function (insurance) {
                        let row = [
                            insurance.checkId,
                            `${insurance.accountName} - ${insurance.accountMemberNumber}`,
                            insurance.employeeName,
                            insurance.registration
                        ];

                        if (currentTab === 0) {
                            row.push(
                                getColouredAIStatus(insurance.aiStatus),
                                <DateToLocal key={`createddate-${insurance.checkId}`} format='DD/MM/YYYY'>{insurance.createdDate}</DateToLocal>,
                                <DateToLocal key={`modifieddate-${insurance.checkId}`} format='DD/MM/YYYY'>{insurance.modifiedDate}</DateToLocal>,
                                <Button key={`view-${insurance.checkId}`} text="View" intent="primary" onClick={() => openInsuranceCheckView(insurance)} />
                            );
                        }
                        else {
                            row.push(
                                insurance.aiStatus,
                                <DateToLocal key={`approvaldate-${insurance.checkId}`} format='DD/MM/YYYY'>{insurance.humanApprovalDate}</DateToLocal>,
                                getHumanResult(insurance.humanApproved),
                                <LinkButton
                                    key={`${insurance.checkId}-view-insurance`}
                                    href={`/internal-admin/grey-fleet-insurance/${insurance.insuranceDocumentId}`}
                                    text="View"
                                    intent={Intent.PRIMARY}
                                    large={false}
                                />
                            );
                        }

                        return row;
                    }));
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "List of insurances could not be loaded. Please try loading the page again.");
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "List of insurances could not be loaded. Please try loading the page again.");
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>{title}</h1>
                    <div className="spacer-bottom">
                        <Tabs tabs={tabs} onClick={onTabClick} disabled={loadingData} />
                    </div>

                    <div className="filter-controls">
                        <div className="inline-items spacer-bottom-small">
                            <div className="inline-item fill">
                                <div className="form-field-full">
                                    <FormTextInput
                                        inputRef={searchInputRef}
                                        maxLength={50}
                                        placeholder="Employee name or Vehicle registration"
                                        onChange={onSearchChange}
                                        value={searchTerm}
                                        large
                                        disabled={loadingData}
                                        icon="search"
                                        id="insurance-search-field" />
                                </div>
                            </div>
                            <div className="inline-item">
                                <FormListSelector
                                    itemsList={accounts}
                                    selectedItems={selectedAccounts}
                                    setSelectedItems={onAccountsSelected}
                                    searchPlaceholder="Account Name or Member Number"
                                    itemsName="Accounts"
                                    itemsIcon='inbox'
                                    loading={loadingData || accounts.length === 0}
                                    onListOpened={onAccountsListOpened}
                                    onListClose={onAccountsListClose}
                                />
                            </div>
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>                            
                        </div>
                        <Filter filterName={filtersReduxName} visible={showFilters} filters={pageFilters} onUpdate={onFilterChange} loading={loadingData} onClickOutside={onToggleFilter} />
                    </div>

                    <ListingTable
                        id="listing-table-gf-insurance"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={loadingData}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                        pageSize={pageSize}
                    />

                    <GreyFleetInsuranceModal
                        insurance={selectedInsurance}
                        nextInsuranceId={nextInsuranceId}
                        isOpen={showInsuranceModal && currentTab === 0 && selectedInsurance != null}
                        onClose={() => setShowInsuranceModal(false)}
                        onNext={showNextInsuranceModal}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

GreyFleetInsuranceListing.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  requiredAction: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};
